import CheckIcon from "@mui/icons-material/Check";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";

import axios, { HttpStatusCode } from "axios";
import LoadingButton from "common/components/loading-button";
import EmailAddress from "common/values/email-address/email-address";
import EmailAddressInput from "common/values/email-address/view/email-address-input";
import Guid from "common/values/guid/guid";
import Name from "common/values/name/name";
import NameInput from "common/values/name/view/name-input";
import { EntityNameInput } from "legal-entities/components";
import EntityAgreement from "legal-entities/entities/entity-agreement/entity-agreement";
import EntityName from "legal-entities/values/entity-name/entity-name";
import { useNavigate, useParams } from "react-router";
import { useSession } from "users/session/session-context";
import UserInformationAPIService from "users/values/user-information/api/user-information-api-service";
import EntityAgreementAPIService, { EntityAgreementNotFoundError, InvalidEntityAgreementError } from "../api/entity-agreement-api-service";
import { KeyboardKey } from "common/helpers/keyboard-keys";

const PageContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  overflow: 'auto',
  padding: theme.spacing(4, 2),
  textAlign: "center"
}));
const Title = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(4)
}));
const CheckList = styled(List)(({ theme }) => ({
  margin: "auto",
  maxWidth: theme.spacing(80)
}));
const Instructions = styled(Typography)(({ theme }) => ({
  margin: theme.spacing(2, "auto", 1),
  maxWidth: theme.spacing(100)
}));
const ErrorList = styled(Box)(({ theme }) => ({
  color: theme.palette.error.main,
  textAlign: "center"
}));
const ErrorText = styled(Typography)(({ theme }) => ({
  margin: theme.spacing(0.5),
}));
const AcceptButton = styled(LoadingButton)(({ theme }) => ({
  marginTop: theme.spacing(2)
}));
const HelperText = styled(Container)(({ theme }) => ({
  color: theme.palette.text.secondary,
  margin: theme.spacing(2, 'auto'),
  padding: theme.spacing(2)
}));

type EntityAgreementFormErrors = {
  general?: string;
  email?: string;
  name?: string;
  entityName?: string;
}

export default function EntityAgreementPage() {
  const [entityAgreement, setEntityAgreement] = useState<EntityAgreement>();
  const [formEntityName, setFormEntityName] = useState<EntityName>();
  const [formAdministratorName, setFormAdministratorName] = useState<Name>();
  const [formAdministratorEmail, setFormAdministratorEmail] = useState<EmailAddress>();
  const [formSkipAdminCreation, setFormSkipAdminCreation] = useState<boolean>(false);
  const [formEntityIsClient, setFormEntityIsClient] = useState<boolean>(true);
  const [formEntityIsVendor, setFormEntityIsVendor] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<EntityAgreementFormErrors>();
  const [saving, setSaving] = useState<boolean>(false);

  const session = useSession();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();

  useEffect(() => {
    async function loadAgreement() {
      try {
        let agreement: EntityAgreement;
        const sessionAgreement = session.openEntityAgreements.find(
          (agreement: EntityAgreement) => agreement.id.value === params.id
        );

        if (sessionAgreement) {
          agreement = sessionAgreement;
        } else {
          if (!params.id) throw new Error("No agreement ID provided");
          const service = new EntityAgreementAPIService(session);
          const abortController = new AbortController();
          agreement = await service.getEntityAgreementById(new Guid(params.id), abortController);
        }

        setEntityAgreement(agreement);
        setFormEntityName(agreement.entityName);
      } catch (error) {
        console.error(error);
        enqueueSnackbar("Couldn't get agreement. Please try again.", { variant: "error" });
      }
    }
    if (!entityAgreement) {
      void loadAgreement();
      return;
    }
    setFormEntityName(entityAgreement.entityName);
    setFormAdministratorName(entityAgreement.administratorName);
    setFormAdministratorEmail(entityAgreement.administratorEmail);
  }, [entityAgreement, params.id, session.openEntityAgreements]);

  function rebuildAgreement(
    entityName?: EntityName,
    emailAddress?: EmailAddress,
    name?: Name,
    errors?: EntityAgreementFormErrors
  ): EntityAgreement | undefined {
    if (!entityAgreement) throw EntityAgreementNotFoundError;
    if (!formValid(errors) || !entityName) return;

    try {
      const updatedEntityName = entityName ?? entityAgreement.entityName
      let updatedAgreement = new EntityAgreement(entityAgreement.id, updatedEntityName);
      updatedAgreement.termsFileId = entityAgreement.termsFileId;

      if (!formSkipAdminCreation) {
        updatedAgreement.administratorEmail = emailAddress;
        updatedAgreement.administratorName = name;
      }

      return updatedAgreement;
    } catch (error) {
      if (error instanceof InvalidEntityAgreementError) {
        let errors: EntityAgreementFormErrors = {};
        errors.general = error.message;
        setFormErrors(errors);
        return;
      }
      throw error;
    }
  }

  async function handleAcceptAgreement() {
    setSaving(true);

    const builtAgreement = rebuildAgreement(formEntityName, formAdministratorEmail, formAdministratorName);
    let abortController = new AbortController();

    try {
      if (!entityAgreement) throw EntityAgreementNotFoundError;
      if (!builtAgreement) throw new InvalidEntityAgreementError("Agreement is invalid");
      const service = new EntityAgreementAPIService(session);
      const finalAgreement = await service.updateEntityAgreement(
        entityAgreement,
        builtAgreement
      );

      const authResponse = await service.acceptEntityAgreement(
        finalAgreement,
        formEntityIsClient,
        formEntityIsVendor
      );

      session.initializeFromAuthResponse(authResponse);

      if (!session.company?.entityId) {
        await session.refresh(new UserInformationAPIService(session), abortController);
      }

      navigate("/dashboard", { replace: true });
    } catch (error: unknown) {
      if (axios.isCancel(error)) {
        navigate('/dashboard');
        return;
      }

      console.error(error);

      let message = "Couldn't accept agreement. Please try again";
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.BadRequest) {
        message = String(error.response?.data);
      }

      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setSaving(false);
    }
  }

  function formValid(errors?: EntityAgreementFormErrors): boolean {
    errors ??= formErrors;

    const hasRole = formEntityIsClient || formEntityIsVendor;

    const optionalAdminCheck = formSkipAdminCreation ||
      (formAdministratorEmail !== undefined && formAdministratorName !== undefined);

    return (
      formEntityName !== undefined &&
      hasRole &&
      optionalAdminCheck &&
      !errors?.entityName &&
      !errors?.general &&
      (formSkipAdminCreation || (
        (!formSkipAdminCreation && !errors?.email) &&
        (!formSkipAdminCreation && !errors?.name)
      ))
    )
  }

  function updateFormErrors(type: keyof EntityAgreementFormErrors, error?: string) {
    let errors: EntityAgreementFormErrors = { ...formErrors };

    if (type === 'entityName') {
      errors.entityName = error;
    }
    else if (type === 'email') {
      errors.email = error;
    }
    else if (type === 'name') {
      errors.name = error;
    }
    else {
      errors.general = error;
    }

    setFormErrors(errors);
    return errors;
  }

  return (
    <PageContainer>
      <Grid container direction="column">
        <Grid component="form">
          <Title variant="h4">Terms Of Use</Title>
          <EntityNameInput
            label="Organization Name"
            entityName={formEntityName}
            variant="outlined"
            onChange={(updatedEntityName?: EntityName, error?: string) => {
              if (updatedEntityName) setFormEntityName(updatedEntityName);
              const errors = updateFormErrors('entityName', error);
              rebuildAgreement(updatedEntityName, formAdministratorEmail, formAdministratorName, errors);
            }}
          />
        </Grid>
        <Grid>
          <CheckList disablePadding>
            <ListItem secondaryAction={
              <Tooltip
                disableFocusListener
                title={
                  "Setting this option will allow your Legal Entity to act as a client on the Marketplace. " +
                  "This option can be enabled later. "
                }
                placement="bottom"
                arrow>
                <IconButton size="small">
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            }>
              <ListItemButton
                disableGutters
                onClick={() => setFormEntityIsClient(!formEntityIsClient)}>
                <ListItemIcon>
                  <Checkbox
                    id="entity-agreement-is-client-checkbox"
                    edge="start"
                    color="primary"
                    checked={formEntityIsClient}
                    onChange={(event) =>
                      setFormEntityIsClient(event.target.checked)
                    }
                  />
                </ListItemIcon>
                <ListItemText
                  primary={`Client: ${formEntityIsClient
                    ? "This Legal Entity will utilize services offered on the Marketplace."
                    : "Allow your Legal Entity to purchase and use legal services offered on the Marketplace?"
                    }`}
                />
              </ListItemButton>
            </ListItem>
            <ListItem secondaryAction={
              <Tooltip
                disableFocusListener
                title={
                  "Setting this option will allow your Legal Entity to act as a Vendor on the Marketplace. " +
                  "This option can be enabled later. "
                }
                placement="bottom"
                arrow>
                <IconButton size="small">
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            }>
              <ListItemButton
                disableGutters
                onClick={() => setFormEntityIsVendor(!formEntityIsVendor)}>
                <ListItemIcon>
                  <Checkbox
                    id="entity-agreement-is-vendor-checkbox"
                    edge="start"
                    color="primary"
                    checked={formEntityIsVendor}
                    onChange={(event) =>
                      setFormEntityIsVendor(event.target.checked)
                    }
                  />
                </ListItemIcon>
                <ListItemText
                  primary={`Vendor: ${formEntityIsVendor
                    ? "This Legal Entity will offer services on the Marketplace."
                    : "Allow your Legal Entity to offer legal services on the Marketplace?"
                    }`}
                />
              </ListItemButton>
            </ListItem>
            <ListItem secondaryAction={
              <Tooltip
                disableFocusListener
                title={
                  "You can skip creating an additional Legal Entity Administrator fow now, " +
                  "and create one or more administrators at a later date"
                }
                placement="bottom"
                arrow>
                <IconButton size="small">
                  <InfoIcon />
                </IconButton>
              </Tooltip>

            }>
              <ListItemButton
                disableGutters
                onClick={() =>
                  setFormSkipAdminCreation(!formSkipAdminCreation)
                }>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    color="primary"
                    checked={!formSkipAdminCreation}
                    onChange={(event) =>
                      setFormSkipAdminCreation(!event.target.checked)
                    }
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    !formSkipAdminCreation
                      ? "Creating Additional Entity Administrator"
                      : "Optional: Create Additional Entity Administrator?"
                  }
                />
              </ListItemButton>
            </ListItem>
            {!formSkipAdminCreation && (
              <ListItem disableGutters>
                <Instructions>
                  Enter contact details for another person who is legally
                  able to sign contracts on behalf of the organization:
                </Instructions>
              </ListItem>
            )}
          </CheckList>
        </Grid>
        {!formSkipAdminCreation && (
          <Grid component="form">
            <NameInput
              idPrefix="entity-agreement-admin"
              variant="outlined"
              name={formAdministratorName}
              onChange={(updatedName?: Name, error?: string) => {
                if (updatedName) setFormAdministratorName(updatedName);
                const errors = updateFormErrors('name', error);
                rebuildAgreement(formEntityName, formAdministratorEmail, updatedName, errors);
              }}
            />
            <EmailAddressInput
              idPrefix="entity-agreement-admin"
              variant="outlined"
              emailAddress={formAdministratorEmail}
              onChange={(updatedEmailAddress?: EmailAddress, error?: string) => {
                if (updatedEmailAddress) setFormAdministratorEmail(updatedEmailAddress);
                const errors = updateFormErrors('email', error);
                rebuildAgreement(formEntityName, updatedEmailAddress, formAdministratorName, errors);
              }}
              onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                if (event.key === KeyboardKey.Enter && formValid()) {
                  void handleAcceptAgreement();
                }
              }}
            />
          </Grid>
        )}
        <Grid component="form">
          {!formEntityIsClient && !formEntityIsVendor && (
            <Grid>
              <ErrorList>
                <ErrorText>
                  Your Legal Entity must select Client, Vendor or both to
                  Accept Agreement
                </ErrorText>
              </ErrorList>
            </Grid>
          )}
          <AcceptButton
            id="entity-agreement-submit-button"
            variant="contained"
            color="primary"
            loading={saving}
            disabled={saving || !formValid()}
            startIcon={<CheckIcon />}
            onClick={handleAcceptAgreement}>
            Accept Agreement
          </AcceptButton>
        </Grid>
        <Grid>
          <HelperText>
            <Typography>
              By clicking the accept button above you agree to be bound by
              the following terms
            </Typography>
          </HelperText>
        </Grid>
        <Grid>
          {/* <LegalEntityTerms termsId={this.state.termsId} /> */}
        </Grid>
      </Grid>
    </PageContainer>
  );
}
